/**
 * PipelineMetrics Component
 *
 * Shows metrics panel below a pipeline step box.
 */

import { Clock, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QualityIndicator {
  label: string;
  value: string;
  status: string;
}

interface PipelineMetricsProps {
  count: number;
  percentageFromPrevious: number | null;
  lastActivity: string | null;
  qualityIndicator?: QualityIndicator | null;
}

const qualityColors: Record<string, string> = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
  gray: 'text-gray-500'
};

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function PipelineMetrics({
  count,
  percentageFromPrevious,
  lastActivity,
  qualityIndicator
}: PipelineMetricsProps) {
  return (
    <div className="mt-2 text-xs text-muted-foreground space-y-1 min-w-[140px]">
      {/* Count & Percentage */}
      <div className="flex items-center justify-between">
        <span>Count:</span>
        <span className="font-medium">{count.toLocaleString()}</span>
      </div>

      {percentageFromPrevious !== null && (
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            Flow:
          </span>
          <span className={cn(
            'font-medium',
            percentageFromPrevious >= 80 && 'text-green-600',
            percentageFromPrevious >= 50 && percentageFromPrevious < 80 && 'text-yellow-600',
            percentageFromPrevious < 50 && 'text-red-600'
          )}>
            {percentageFromPrevious}%
          </span>
        </div>
      )}

      {/* Last Activity */}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Last:
        </span>
        <span>{formatTimeAgo(lastActivity)}</span>
      </div>

      {/* Quality Indicator */}
      {qualityIndicator && (
        <div className="flex items-center justify-between">
          <span>Quality:</span>
          <span className={cn('font-medium', qualityColors[qualityIndicator.status] || 'text-gray-500')}>
            {qualityIndicator.label}
          </span>
        </div>
      )}
    </div>
  );
}
